import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';

interface Viewer {
  userId: string;
  username: string;
  joinedAt: string;
}

export const useViewerPresence = (streamId: string, currentUserId?: string) => {
  const [viewers, setViewers] = useState<Viewer[]>([]);
  const [viewerCount, setViewerCount] = useState(0);
  const [channel, setChannel] = useState<RealtimeChannel | null>(null);

  useEffect(() => {
    if (!streamId) return;

    let isSubscribed = true;
    let updateInterval: NodeJS.Timeout;

    const presenceChannel = supabase.channel(`stream:${streamId}:presence`, {
      config: {
        presence: {
          key: currentUserId || `anon_${Math.random().toString(36).substr(2, 9)}`,
        },
      },
    });

    presenceChannel
      .on('presence', { event: 'sync' }, () => {
        if (!isSubscribed) return;
        
        const state = presenceChannel.presenceState();
        const viewerList = Object.values(state)
          .flat()
          .map((viewer: any) => ({
            userId: viewer.userId || 'anonymous',
            username: viewer.username || 'Anonymous',
            joinedAt: viewer.joinedAt,
          }));
        
        setViewers(viewerList);
        setViewerCount(viewerList.length);
      })
      .on('presence', { event: 'join' }, ({ newPresences }) => {
        if (!isSubscribed) return;
        console.log('Viewer joined:', newPresences);
      })
      .on('presence', { event: 'leave' }, ({ leftPresences }) => {
        if (!isSubscribed) return;
        console.log('Viewer left:', leftPresences);
      })
      .subscribe(async (status) => {
        if (!isSubscribed) return;
        
        if (status === 'SUBSCRIBED') {
          if (currentUserId) {
            try {
              // Get user profile for presence
              const { data: profile } = await supabase
                .from('profiles')
                .select('username, display_name')
                .eq('id', currentUserId)
                .maybeSingle();

              await presenceChannel.track({
                userId: currentUserId,
                username: profile?.display_name || profile?.username || 'Anonymous',
                joinedAt: new Date().toISOString(),
              });
            } catch (error) {
              console.error('Error tracking presence:', error);
            }
          } else {
            // Track anonymous viewer
            await presenceChannel.track({
              userId: 'anonymous',
              username: 'Anonymous Viewer',
              joinedAt: new Date().toISOString(),
            });
          }
        }
      });

    setChannel(presenceChannel);

    // Update viewer count in database periodically with debounce
    updateInterval = setInterval(async () => {
      if (!isSubscribed || viewerCount === 0) return;
      
      try {
        await supabase
          .from('streams')
          .update({ viewer_count: viewerCount })
          .eq('id', streamId);
      } catch (error) {
        console.error('Error updating viewer count:', error);
      }
    }, 10000); // Update every 10 seconds instead of 5 for better performance

    return () => {
      isSubscribed = false;
      clearInterval(updateInterval);
      if (presenceChannel) {
        presenceChannel.untrack().then(() => {
          supabase.removeChannel(presenceChannel);
        });
      }
    };
  }, [streamId, currentUserId, viewerCount]);

  return { viewers, viewerCount, channel };
};
